/**
 * SelectedRoomItemSection — native "added items" summary (lifted from
 * `common/StagingFlow/SelectedRoomItemSection/SelectedRoomItemSection.component.tsx`,
 * rewired onto the module working context + `patchRoom` + plain-JS room arrays).
 * Reuses the context-free `SelectedStagingItemPreview` (`components/common/*`).
 */

import { BEIGE_600, GRAY_900 } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'
import { FC, useMemo } from 'react'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { ChildrenWithDivider } from 'components/common/ChildrenWithDivider'
import { SelectedStagingItemPreview } from 'components/common/StagingFlow/SelectedStagingItemPreview'
import { VirtualFurnitureRoomType } from 'constants/virtualFurniture'
import { stagingDecorationLink } from 'constants/staticLinks'
import { useTranslation } from 'react-i18next'
import { useVirtualFurnitureData } from 'components/contexts/VirtualFurnitureData.context'

import { RoomItemPopupType, useStagingWorkingState } from '../StagingWorkingState.context'
import { mapDelete, mapGet } from '../stagingRoom.logic'

const SelectedItemDescription: FC<{ title: string; content: string }> = ({ title, content }) => (
  <>
    {`${title}: `}
    <Typography variant="text-sm" color={GRAY_900}>
      {content}
    </Typography>
  </>
)

export const SelectedRoomItemSection: FC = () => {
  const { t } = useTranslation(['style_selection_order_step', 'common', 'staging_decorations', 'virtual_furniture_style_category'])
  const { virtualFurnitureStylesMap } = useVirtualFurnitureData()
  const { activeRoom, setRoomItemPopupType, patchRoom } = useStagingWorkingState()

  const selectedFurniture = useMemo(
    () => (activeRoom?.styleCodeMap ?? []).map(([, styleCode]) => virtualFurnitureStylesMap[styleCode]).filter(Boolean),
    [activeRoom?.styleCodeMap, virtualFurnitureStylesMap]
  )

  const selectedDecorationsStringList = useMemo(
    () => (activeRoom?.decorations ?? []).map((decoration) => t(`staging_decorations:${decoration}`)).join(', '),
    [activeRoom?.decorations, t]
  )

  const selectedFlooring = useMemo(() => {
    const selectedCode = mapGet(activeRoom?.renovations ?? [], VirtualFurnitureRoomType.FLOORING)
    if (!selectedCode) return undefined
    return virtualFurnitureStylesMap[selectedCode]
  }, [activeRoom?.renovations, virtualFurnitureStylesMap])

  const selectedWallPainting = useMemo(() => {
    const selectedCode = mapGet(activeRoom?.renovations ?? [], VirtualFurnitureRoomType.WALL_MATERIALS)
    if (!selectedCode) return undefined
    return virtualFurnitureStylesMap[selectedCode]
  }, [activeRoom?.renovations, virtualFurnitureStylesMap])

  if (!activeRoom) return null

  return (
    <BorderBoxWrapper padding={2} height="100%" width="100%">

      <Stack>

        <Box paddingBottom="1.2rem" borderBottom={`.1rem solid ${BEIGE_600}`} marginBottom="1.6rem">
          <Typography variant="text-md" fontWeight="semiBold" color={GRAY_900}>
            {t('style_selection_order_step:added_items')}
          </Typography>
        </Box>

        <ChildrenWithDivider dividerProps={{ margin: 12 }}>

          {selectedFurniture.map(({ stagingImageUrl, style, roomType, code }) => (
            <SelectedStagingItemPreview
              key={code}
              imageUrl={stagingImageUrl}
              title={t('style_selection_order_step:room_items.FURNITURE')}
              description={
                <SelectedItemDescription
                  title={t('style_selection_order_step:style')}
                  content={t(`virtual_furniture_style_category:${style}`)}
                />
              }
              onEdit={() => setRoomItemPopupType(RoomItemPopupType.FURNITURE)}
              onDelete={() => patchRoom(activeRoom.key, { styleCodeMap: mapDelete(activeRoom.styleCodeMap, roomType) })}
            />
          ))}

          {activeRoom.decorations.length > 0 &&
            <SelectedStagingItemPreview
              imageUrl={stagingDecorationLink}
              title={t('style_selection_order_step:room_items.DECORATION')}
              description={
                <SelectedItemDescription
                  title={t('style_selection_order_step:item')}
                  content={selectedDecorationsStringList}
                />
              }
              onEdit={() => setRoomItemPopupType(RoomItemPopupType.DECORATION)}
              onDelete={() => patchRoom(activeRoom.key, { decorations: [] })}
            />
          }

          {selectedFlooring &&
            <SelectedStagingItemPreview
              imageUrl={selectedFlooring.stagingImageUrl}
              title={t('style_selection_order_step:room_items.FLOOR')}
              description={
                <SelectedItemDescription
                  title={t('style_selection_order_step:style')}
                  content={t(`virtual_furniture_style_category:${selectedFlooring.style}`)}
                />
              }
              onEdit={() => setRoomItemPopupType(RoomItemPopupType.FLOOR)}
              onDelete={() => patchRoom(activeRoom.key, { renovations: mapDelete(activeRoom.renovations, VirtualFurnitureRoomType.FLOORING) })}
            />
          }

          {selectedWallPainting &&
            <SelectedStagingItemPreview
              imageUrl={selectedWallPainting.stagingImageUrl}
              title={t('style_selection_order_step:room_items.WALL')}
              description={
                <SelectedItemDescription
                  title={t('style_selection_order_step:style')}
                  content={t(`virtual_furniture_style_category:${selectedWallPainting.style}`)}
                />
              }
              onEdit={() => setRoomItemPopupType(RoomItemPopupType.WALL)}
              onDelete={() => patchRoom(activeRoom.key, { renovations: mapDelete(activeRoom.renovations, VirtualFurnitureRoomType.WALL_MATERIALS) })}
            />
          }

        </ChildrenWithDivider>

      </Stack>

    </BorderBoxWrapper>
  )
}
